import {
    getCategorizedRulesAndTotals,
    isMoneyMapIncomeSplitEligible,
    MoneyMapCategorySection,
    MoneyMapFilterState,
    MoneyMapVisual,
} from '@fruitful/money-map';
import {
    AnalyticsUserEvent,
    MoneyMapCategory,
    type MoneyMapRuleWithAccountDetails,
    type MoneyMapWithRulesAndAccounts,
} from '@fruitful/types';
import { Redirect } from 'expo-router';
import { useMemo, useState } from 'react';
import {
    ArrowLeftIcon,
    ArrowRightIcon,
    Button,
    ButtonSize,
    ButtonVariant,
    FullScreenSpinner,
    SmoothieScrollView,
    XStack,
    YStack,
} from 'smoothie';

import { captureWarning, track } from '../../../logger/Logger';
import { useGetMembershipMoneyMapAccounts } from '../../hooks/useGetMembershipMoneyMapAccounts';
import { MoneyMapVisualExplainer } from './MoneyMapVisualExplainer';

interface MoneyMapProps {
    moneyMap?: MoneyMapWithRulesAndAccounts;
    showSubmitButton?: boolean;
    submitButtonText?: string;
    onPressSubmit?: () => void;
}

const MoneyMap = ({
    moneyMap,
    showSubmitButton,
    submitButtonText,
    onPressSubmit,
}: MoneyMapProps) => {
    const { data: accounts, isLoading: isLoadingAccounts } =
        useGetMembershipMoneyMapAccounts({});

    const [filter, setFilter] = useState(MoneyMapFilterState.ALL);

    const {
        billRules,
        spendRules,
        goalRules,
        otherAccountRules,
        billsTotalCents,
        spendTotalCents,
        goalsTotalCents,
    } = useMemo(() => {
        return getCategorizedRulesAndTotals(moneyMap, accounts);
    }, [JSON.stringify(accounts), JSON.stringify(moneyMap)]);

    if (isLoadingAccounts) {
        return <FullScreenSpinner />;
    }

    if (moneyMap == null || accounts == null) {
        captureWarning('Could not fetch money map and account data');
        return <Redirect href="/roadmap/go/start" />;
    }

    const showBills =
        billRules != null &&
        billsTotalCents != null &&
        (filter === MoneyMapFilterState.ALL ||
            filter === MoneyMapFilterState.BILLS);
    const showSpend =
        spendRules != null &&
        spendTotalCents != null &&
        (filter === MoneyMapFilterState.ALL ||
            filter === MoneyMapFilterState.SPEND);
    const showGoals =
        goalRules != null &&
        goalsTotalCents != null &&
        (filter === MoneyMapFilterState.ALL ||
            filter === MoneyMapFilterState.GOALS);
    // Keep Other accounts out of the chart's Bills/Spend/Goals filter states.
    // It only renders in the unfiltered "All" view, since it is not part of the routing totals.
    const showOtherAccounts =
        otherAccountRules.length > 0 && filter === MoneyMapFilterState.ALL;

    const isIncomeSplitEligible = isMoneyMapIncomeSplitEligible(
        moneyMap,
        accounts,
    );

    return (
        <SmoothieScrollView
            containerStyle={{
                width: '100%',
                maxWidth: 700,
                alignSelf: 'center',
                paddingHorizontal: 24,
                justifyContent: 'space-between',
                '$platform-web': {
                    marginTop: 0,
                },
            }}
        >
            <YStack gap={8} marginVertical={24}>
                <YStack gap={4}>
                    <MoneyMapVisual
                        incomeTotalCents={moneyMap.monthlyIncomeCents}
                        billsTotalCents={billsTotalCents}
                        spendTotalCents={spendTotalCents}
                        goalsTotalCents={goalsTotalCents}
                        filter={filter}
                        setFilter={setFilter}
                        isIncomeSplitEligible={isIncomeSplitEligible}
                    />
                    {isIncomeSplitEligible && (
                        <MoneyMapVisualExplainer filter={filter} />
                    )}
                </YStack>
                {showBills && (
                    <MoneyMapCategorySection
                        category={MoneyMapCategory.BILLS}
                        moneyMap={moneyMap}
                        accounts={accounts}
                        totalAmountCents={billsTotalCents}
                        rules={billRules as MoneyMapRuleWithAccountDetails[]}
                    />
                )}
                {showSpend && (
                    <MoneyMapCategorySection
                        category={MoneyMapCategory.SPEND}
                        moneyMap={moneyMap}
                        accounts={accounts}
                        totalAmountCents={spendTotalCents}
                        rules={spendRules as MoneyMapRuleWithAccountDetails[]}
                    />
                )}
                {showGoals && (
                    <MoneyMapCategorySection
                        category={MoneyMapCategory.GOALS}
                        moneyMap={moneyMap}
                        accounts={accounts}
                        totalAmountCents={goalsTotalCents}
                        rules={goalRules as MoneyMapRuleWithAccountDetails[]}
                    />
                )}
                {showOtherAccounts && (
                    <MoneyMapCategorySection
                        category={MoneyMapCategory.OTHER_ACCOUNTS}
                        moneyMap={moneyMap}
                        accounts={accounts}
                        totalAmountCents={null}
                        rules={
                            otherAccountRules as MoneyMapRuleWithAccountDetails[]
                        }
                    />
                )}
            </YStack>
            <YStack marginBottom={24}>
                {showSubmitButton &&
                    (filter === MoneyMapFilterState.ALL ||
                        filter === MoneyMapFilterState.GOALS) && (
                        <Button
                            variant={ButtonVariant.PRIMARY}
                            size={ButtonSize.LARGE}
                            text={submitButtonText ?? 'Continue'}
                            onPress={onPressSubmit}
                        />
                    )}
                {!showSubmitButton && filter === MoneyMapFilterState.GOALS && (
                    <XStack
                        alignItems="center"
                        justifyContent="flex-start"
                        marginHorizontal={12}
                    >
                        <Button
                            variant={ButtonVariant.SECONDARY}
                            size={ButtonSize.SMALL}
                            text="Spend"
                            width="auto"
                            icon={ArrowLeftIcon}
                            iconPosition="left"
                            onPress={() => {
                                track(
                                    AnalyticsUserEvent.MONEY_MAP_CHART_INTERACTION,
                                    {
                                        action: 'filter',
                                        filter: 'spend',
                                    },
                                );
                                setFilter(MoneyMapFilterState.SPEND);
                            }}
                        />
                    </XStack>
                )}
                {filter === MoneyMapFilterState.BILLS && (
                    <XStack
                        alignItems="center"
                        justifyContent="flex-end"
                        marginHorizontal={12}
                    >
                        <Button
                            variant={ButtonVariant.SECONDARY}
                            size={ButtonSize.SMALL}
                            text="Spend"
                            width="auto"
                            icon={ArrowRightIcon}
                            iconPosition="right"
                            onPress={() => {
                                track(
                                    AnalyticsUserEvent.MONEY_MAP_CHART_INTERACTION,
                                    {
                                        action: 'filter',
                                        filter: 'spend',
                                    },
                                );
                                setFilter(MoneyMapFilterState.SPEND);
                            }}
                        />
                    </XStack>
                )}
                {filter === MoneyMapFilterState.SPEND && (
                    <XStack
                        alignItems="center"
                        justifyContent="space-between"
                        marginHorizontal={12}
                    >
                        <Button
                            variant={ButtonVariant.SECONDARY}
                            size={ButtonSize.SMALL}
                            text="Bills"
                            width="auto"
                            icon={ArrowLeftIcon}
                            iconPosition="left"
                            onPress={() => {
                                track(
                                    AnalyticsUserEvent.MONEY_MAP_CHART_INTERACTION,
                                    {
                                        action: 'filter',
                                        filter: 'bills',
                                    },
                                );
                                setFilter(MoneyMapFilterState.BILLS);
                            }}
                        />
                        <Button
                            variant={ButtonVariant.SECONDARY}
                            size={ButtonSize.SMALL}
                            text="Goals"
                            width="auto"
                            icon={ArrowRightIcon}
                            iconPosition="right"
                            onPress={() => {
                                track(
                                    AnalyticsUserEvent.MONEY_MAP_CHART_INTERACTION,
                                    {
                                        action: 'filter',
                                        filter: 'goals',
                                    },
                                );
                                setFilter(MoneyMapFilterState.GOALS);
                            }}
                        />
                    </XStack>
                )}
            </YStack>
        </SmoothieScrollView>
    );
};

export default MoneyMap;
